<template>
  <form class="form" ref="form">
    <div class="form-grid">
      <div class="form-field">
        <label for="oldPassword">{{ labelOldPassword }}</label>
        <PasswordInput
          id="oldPassword"
          :model-value="oldPassword"
          @update:model-value="(val) => (oldPassword = val)"
          required
        />
        <span v-if="errors['oldPassword']" class="error">{{
          errors['oldPassword']
        }}</span>
      </div>
      <div class="form-field">
        <label for="newPassword">Новый пароль</label>
        <PasswordInput
          id="newPassword"
          :model-value="newPassword"
          @update:model-value="(val) => (newPassword = val)"
          required
        />
        <span v-if="errors['newPassword']" class="error">{{
          errors['newPassword']
        }}</span>
      </div>
      <div class="form-field">
        <label for="repeatPassword">Подтверждение нового пароля</label>
        <PasswordInput
          id="repeatPassword"
          :model-value="repeatPassword"
          @update:model-value="(val) => (repeatPassword = val)"
          required
        />
        <span v-if="errors['repeatPassword']" class="error">{{
          errors['repeatPassword']
        }}</span>
      </div>
      <div class="password-requirements">
        <p class="requirements-title">Требования к новому паролю</p>
        <ul class="requirements-list">
          <li>Длина: от 10 символов</li>
          <li>
            Состав: должны присутствовать 3 вида символов:
            <ul>
              <li>Латинские буквы (A–Z, a–z)</li>
              <li>Цифры (0–9)</li>
              <li>Спецсимволы (например, #, $, %, &)</li>
            </ul>
          </li>
          <li>История паролей: Нельзя использовать свой текущий пароль</li>
          <li>Лимит на смену: 1 раз в 24 часа</li>
        </ul>
      </div>
      <button
        class="btn btn-next"
        type="submit"
        :disabled="isLoading"
        @click="onSubmit"
      >
        Сохранить
      </button>
    </div>
  </form>
</template>

<script setup>
import { passwordChange } from '@/api/account'
import { requiredText } from '@/constants'
import { useForm } from 'vee-validate'
import { useStore } from 'vuex'
import { ref } from 'vue'
import { object, string, ref as yupRef } from 'yup'
import PasswordInput from '@/components/PasswordInput.vue'

defineProps({
  labelOldPassword: {
    type: String,
    default: 'Старый пароль',
  },
})

const validationSchema = object({
  oldPassword: string().required(requiredText),
  newPassword: string()
    .required(requiredText)
    .min(10, 'Минимальная длина 10 символов')
    .test(
      'password-strength',
      'Пароль должен содержать минимум 3 типа символов: латинские буквы, цифры, спецсимволы',
      (value) => {
        if (!value) return false

        let typesCount = 0

        // Check for Latin letters
        if (/[a-zA-Z]/.test(value)) typesCount++

        // Check for digits
        if (/\d/.test(value)) typesCount++

        // Check for special characters
        if (/[^a-zA-Z\d\s]/.test(value)) typesCount++

        return typesCount >= 3
      }
    )
    .test(
      'not-same-as-old',
      'Нельзя использовать свой текущий пароль',
      (value, context) => {
        const { parent } = context
        return value !== parent.oldPassword
      }
    ),
  repeatPassword: string()
    .required(requiredText)
    .oneOf([yupRef('newPassword')], 'Ваш пароль не совпадает'),
})

const { errors, validate, defineField } = useForm({
  validationSchema,
})

const [oldPassword] = defineField('oldPassword')
const [newPassword] = defineField('newPassword')
const [repeatPassword] = defineField('repeatPassword')
const store = useStore()
const isLoading = ref(false)
const emit = defineEmits(['success'])

/**
 * @param {Event} e
 */
const onSubmit = async (e) => {
  e.preventDefault()

  const { valid } = await validate()
  if (!valid) return

  try {
    isLoading.value = true
    await passwordChange({
      old_password: oldPassword.value,
      new_password: newPassword.value,
      repeat_password: repeatPassword.value,
    })
    emit('success')
  } catch (error) {
    store.dispatch('showError', error)
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.password-requirements {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
}

.requirements-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.requirements-list {
  padding-left: 20px;
  margin: 0;
}

.requirements-list li {
  margin-bottom: 3px;
  list-style: disc;
}

.requirements-list ul {
  padding-left: 15px;
  margin: 3px 0;
}
</style>
